import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { api } from "@/lib/api";

const rs = (v) => "Rs. " + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export function exportPdf(title, columns, rows, subtitle = "") {
  const doc = new jsPDF({ orientation: columns.length > 6 ? "landscape" : "portrait" });
  doc.setFontSize(16);
  doc.text(`SPARKORA EDU — ${title}`, 14, 16);
  doc.setFontSize(9);
  doc.text(`${subtitle}${subtitle ? " • " : ""}Generated ${new Date().toLocaleString("en-IN")}`, 14, 23);
  autoTable(doc, {
    startY: 28,
    head: [columns.map((c) => c.label)],
    body: rows.map((r) => columns.map((c) => (c.money ? rs(r[c.key]) : r[c.key] ?? ""))),
    headStyles: { fillColor: [109, 40, 217] },
    styles: { fontSize: 8 },
  });
  doc.save(`${title.replace(/\s+/g, "-").toLowerCase()}.pdf`);
}

export function exportExcel(title, columns, rows) {
  const data = rows.map((r) => Object.fromEntries(columns.map((c) => [c.label, r[c.key] ?? ""])));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 30));
  XLSX.writeFile(wb, `${title.replace(/\s+/g, "-").toLowerCase()}.xlsx`);
}

export async function downloadSalarySlip(slipId) {
  const { data } = await api.get(`/hr/payslips/${slipId}`);
  const { payslip: p, branch } = data;
  const doc = new jsPDF();
  doc.setFillColor(91, 33, 182);
  doc.rect(0, 0, 210, 30, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.text("SPARKORA EDU", 14, 15);
  doc.setFontSize(10);
  doc.text(`${branch.name || "All branches"} ${branch.code ? `(${branch.code})` : ""}`, 14, 23);
  doc.setFontSize(14);
  doc.text("SALARY SLIP", 196, 15, { align: "right" });
  doc.setFontSize(10);
  doc.text(p.month, 196, 23, { align: "right" });
  doc.setTextColor(30, 30, 30);
  [`Employee: ${p.name}`, `Role: ${p.role.replace("_", " ")}`, `Working days: ${p.working_days}`, `Leave days: ${p.leave_days} (free ${p.free_leaves})`].forEach((t, i) => doc.text(t, 14, 42 + i * 6));
  [`Status: ${p.status.toUpperCase()}`, `Payment date: ${p.payment_date || "-"}`, `Method: ${(p.payment_method || "-").toUpperCase()}`, `Reference: ${p.payment_reference || "-"}`].forEach((t, i) => doc.text(t, 110, 42 + i * 6));
  autoTable(doc, {
    startY: 70,
    head: [["Earnings", "Amount", "Deductions", "Amount"]],
    body: [
      ["Basic salary", rs(p.basic), "Attendance deduction", rs(p.attendance_deduction)],
      ["Allowances", rs(p.allowances), "Leave deduction", rs(p.leave_deduction)],
      ["Incentives", rs(p.incentives), "Loan deduction", rs(p.loan_deduction)],
      ["Overtime", rs(p.overtime), "Advance salary", rs(p.advance_deduction)],
      ...(p.absence_deduction !== undefined ? [["", "", `Absence (${p.absent_days || 0} absent, ${p.half_days || 0} half-day, ${p.late_days || 0} late)`, rs(p.absence_deduction)]] : []),
    ],
    foot: [["Gross earnings", rs(p.gross), "Total deductions", rs(p.total_deductions + p.advance_deduction)], ["", "", "NET SALARY", rs(p.net)]],
    headStyles: { fillColor: [124, 58, 237] },
    footStyles: { fillColor: [245, 243, 255], textColor: [30, 30, 30] },
    columnStyles: { 1: { halign: "right" }, 3: { halign: "right" } },
  });
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("Net Salary = Basic + Allowances + Incentives + Overtime - Deductions - Advances. Computer-generated slip.", 14, 287);
  doc.save(`salary-slip-${p.name.replace(/\s+/g, "-")}-${p.month}.pdf`);
}

export async function downloadFeeReceipt(paymentId) {
  const { data } = await api.get(`/fee-mgmt/payments/${paymentId}/receipt`);
  const { payment: p, plan, branch } = data;
  const inst = plan.installments.find((i) => i.no === p.installment_no) || {};
  const doc = new jsPDF();
  doc.setFillColor(91, 33, 182);
  doc.rect(0, 0, 210, 30, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.text("SPARKORA EDU", 14, 15);
  doc.setFontSize(10);
  doc.text(`${branch.name || ""} ${branch.code ? `(${branch.code})` : ""} ${branch.location ? `- ${branch.location}` : ""}`, 14, 23);
  doc.setFontSize(14);
  doc.text("PAYMENT RECEIPT", 196, 15, { align: "right" });
  doc.setFontSize(10);
  doc.text(p.receipt_no, 196, 23, { align: "right" });
  doc.setTextColor(30, 30, 30);
  const left = [`Student: ${plan.student_name}`, `Admission No: ${plan.admission_number}`, `Class: ${plan.class_name} / ${plan.section}`, `Academic year: ${plan.academic_year}`];
  const right = [`Date: ${new Date(p.received_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, `Mode: ${p.mode.toUpperCase()}`, `Transaction ID: ${p.transaction_id || "-"}`, `Received by: ${p.received_by_name}`];
  left.forEach((t, i) => doc.text(t, 14, 42 + i * 6));
  right.forEach((t, i) => doc.text(t, 110, 42 + i * 6));
  autoTable(doc, {
    startY: 70,
    head: [["Description", "Amount"]],
    body: [[`${inst.label || `Installment ${p.installment_no}`} (due ${inst.due_date || "-"})`, rs(p.amount - (p.late_fee || 0))], ...(p.late_fee ? [["Late fee", rs(p.late_fee)]] : [])],
    foot: [["Amount paid", rs(p.amount)], ["Total fee (year)", rs(plan.net_total)], ["Total paid so far", rs(plan.paid)], ["Pending balance", rs(plan.pending)]],
    headStyles: { fillColor: [124, 58, 237] },
    footStyles: { fillColor: [245, 243, 255], textColor: [30, 30, 30] },
    columnStyles: { 1: { halign: "right" } },
  });
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("Computer-generated receipt for a verified payment.", 14, 287);
  doc.save(`${p.receipt_no}.pdf`);
}
